/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// Remove cart-empty class when product is added
document.addEventListener("on:cart:add", function () {
  const drawer = document.querySelector(".drawer");

  if (drawer.classList.contains("cart-empty")) {
    drawer.classList.remove("cart-empty");
  }
});

// window.addEventListener("on:cart:remove", function (items) {
//   const drawer = document.querySelector(".drawer");

//   console.log(items);

//   // if (!drawer.classList.contains("cart-empty")) {
//   //   drawer.classList.remove("cart-empty");
//   // }
// });

window.addEventListener("CookiebotOnDialogDisplay", function () {
  document.querySelector(
    "#CybotCookiebotDialogBodyLevelButtonCustomize"
  ).innerHTML = 'Parinktys <div class="CybotCookiebotDialogArrow"></div>';

  // Replace privacy policy and cookie policy links
  const aboutContent = document.getElementById(
    "CybotCookiebotDialogDetailBodyContentTextAbout"
  );
  if (aboutContent) {
    const privacyLink = aboutContent.querySelector(
      'a[href*="privatumo-politika"]'
    );
    const cookieLink = aboutContent.querySelector(
      'a[href*="slapuku-politika"]'
    );

    if (privacyLink) {
      privacyLink.href =
        "https://shop.gerovesklinika.lt/policies/privacy-policy";
    }
    if (cookieLink) {
      cookieLink.href = "https://shop.gerovesklinika.lt/pages/slapuku-politika";
    }
  }

  console.log("CookiebotOnDialogDisplay");
});

window.addEventListener("DOMContentLoaded", function () {
  const mainSearch = document.querySelector(".main-search");
  if (mainSearch) {
    theme.manuallyLoadImages(mainSearch);
    document.body.classList.add("show-search");
  }
});

// Contact form

const contactForm = document.querySelector(".contact-form");

// Add class to input when it is filled

if (contactForm) {
  const contactFormInputs = contactForm.querySelectorAll("input");

  contactFormInputs.forEach((input) => {
    input.addEventListener("input", function () {
      if (input.value) {
        input.parentElement.classList.add("contact-form-input--filled");
      }
      // Check if input is invalid
      if (input.validity.valid) {
        input.parentElement.classList.remove("invalid");
        input.parentElement.classList.add("valid");
      }

      if (input.validity.valueMissing) {
        input.parentElement.classList.remove("valid");
        input.parentElement.classList.add("invalid");
      }
    });
  });

  // contact form textarea

  const contactFormTextarea = contactForm.querySelector("textarea");

  contactFormTextarea.addEventListener("input", function () {
    if (contactFormTextarea.value) {
      contactFormTextarea.parentElement.classList.add(
        "contact-form-input--filled"
      );
    }
  });
}

// cart link

document.addEventListener("DOMContentLoaded", function () {
  const cartLink = document.querySelector("[data-cart-link]");

  cartLink.addEventListener("click", function (e) {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
  });

  document.addEventListener("on:cart-drawer:after-open", function () {
    // MakeCommerce.initSnippetRequest(true);
  });

  document.addEventListener("product_added_to_cart", (event) => {
    console.log("cart:add");
  });
});

// Subcategory filters

/**
 * Collection Filters with Infinite Scroll Integration
 *
 * This implementation provides subcategory filtering functionality that works seamlessly
 * with infinite scroll. When products are loaded via infinite scroll, they are automatically
 * filtered based on the currently active filter.
 *
 * Features:
 * - Filters products based on data-collection-filters attribute
 * - Works with infinite scroll - newly loaded products are automatically filtered
 * - Resets infinite scroll when filters change to ensure proper pagination
 * - Uses MutationObserver to detect new products being added to the grid
 * - Dispatches custom events for better integration with infinite scroll
 */
function applyCollectionFilters() {
  const activeFilter = document.querySelector(
    "[data-subcategory-filter].active"
  );

  if (!activeFilter) {
    // No active filter, show all products
    const productBlocks = document.querySelectorAll(".product-block");
    productBlocks.forEach((productBlock) => {
      productBlock.style.display = "block";
    });
    return;
  }

  const filter = activeFilter.dataset.subcategoryFilter;
  const productBlocks = document.querySelectorAll(".product-block");

  productBlocks.forEach((productBlock) => {
    const collectionFilters = productBlock.dataset.collectionFilters;

    if (
      !collectionFilters ||
      collectionFilters === "null" ||
      collectionFilters === "undefined"
    ) {
      productBlock.style.display = "none";
      return;
    }

    try {
      const collectionFiltersArray = JSON.parse(collectionFilters);

      if (collectionFiltersArray && collectionFiltersArray.length > 0) {
        const matches = collectionFiltersArray.some(
          (collectionFilter) => collectionFilter.filtras === filter
        );
        productBlock.style.display = matches ? "block" : "none";
      } else {
        productBlock.style.display = "none";
      }
    } catch (error) {
      console.error(
        "Error parsing collection filters:",
        error,
        "Raw data:",
        collectionFilters
      );
      productBlock.style.display = "none";
    }
  });
}

// Function to reset infinite scroll when filters change
function resetInfiniteScroll() {
  const infiniteScroll = document.querySelector("infinite-scroll");
  if (infiniteScroll && infiniteScroll.reset) {
    infiniteScroll.reset();
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const subcategoryFilters = document.querySelectorAll(
    "[data-subcategory-filter]"
  );
  subcategoryFilters.forEach((subcategoryFilter) => {
    subcategoryFilter.addEventListener("click", function (e) {
      e.preventDefault();

      // if already active, remove active class. Reset filters (all products visible)
      if (subcategoryFilter.classList.contains("active")) {
        subcategoryFilter.classList.remove("active");
        applyCollectionFilters(); // This will show all products
        resetInfiniteScroll(); // Reset infinite scroll when filter is removed
        return;
      }

      // remove other active filters
      const activeFilters = document.querySelectorAll(
        "[data-subcategory-filter].active"
      );
      activeFilters.forEach((activeFilter) => {
        activeFilter.classList.remove("active");
      });

      // add active class to current filter
      subcategoryFilter.classList.add("active");

      // Apply filters to all products (including newly loaded ones)
      applyCollectionFilters();
      resetInfiniteScroll(); // Reset infinite scroll when filter changes
    });
  });
});

// Listen for infinite scroll events to apply filters to newly loaded products
document.addEventListener("DOMContentLoaded", function () {
  // The infinite scroll component will dispatch custom events when products are loaded
  // We listen for these events in the event listeners below

  // Also use MutationObserver to watch for new products being added to the grid
  const productGrids = document.querySelectorAll(".product-grid");
  const observers = []; // Store references to observers for cleanup

  productGrids.forEach((grid) => {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any of the added nodes are product blocks
          let hasNewProducts = false;
          mutation.addedNodes.forEach((node) => {
            if (node.classList && node.classList.contains("product-block")) {
              hasNewProducts = true;
            }
          });

          if (hasNewProducts) {
            // Apply filters to newly added products
            setTimeout(() => {
              applyCollectionFilters();
            }, 100);
          }
        }
      });
    });

    observer.observe(grid, {
      childList: true,
      subtree: false,
    });

    observers.push(observer);
  });

  // Clean up observers when page unloads
  window.addEventListener("beforeunload", function () {
    observers.forEach((observer) => observer.disconnect());
  });
});

// Also listen for the infinite scroll's reinitialize event
document.addEventListener("ajax:loaded", function (event) {
  // Apply filters after AJAX content is loaded
  setTimeout(() => {
    applyCollectionFilters();
  }, 200);
});

// Listen for infinite scroll products loaded event
document.addEventListener("infinite-scroll:products-loaded", function (event) {
  // Apply filters to newly loaded products
  setTimeout(() => {
    applyCollectionFilters();
  }, 100);
});

// When clicked on extra-large-image-with-text-overlay, copy discount code to clipboard

const extraLargeImageWithTextOverlay = document.querySelector(
  ".extra-large-image-with-text-overlay"
);

if (extraLargeImageWithTextOverlay) {
  extraLargeImageWithTextOverlay.addEventListener("click", function () {
    navigator.clipboard.writeText("GEROVE10");
  });
}

// Dynamic cart badge functionality
document.addEventListener("DOMContentLoaded", function () {
  // Function to get or create cart badge
  function getOrCreateCartBadge() {
    let cartBadge = document.querySelector(".main-header__cart-badge");

    // If badge doesn't exist, create it
    if (!cartBadge) {
      // Find the cart link/button where the badge should be added
      const cartLink = document.querySelector("[data-cart-link]");
      if (cartLink) {
        cartBadge = document.createElement("span");
        cartBadge.className = "main-header__cart-badge";
        cartBadge.setAttribute("aria-label", "Cart items count");
        cartLink.appendChild(cartBadge);
      }
    }

    return cartBadge;
  }

  // Function to update cart badge count
  function updateCartBadgeCount() {
    // Get current cart count from Shopify's cart object
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        const itemCount = cart.item_count;
        const cartBadge = getOrCreateCartBadge();

        if (cartBadge) {
          cartBadge.textContent = itemCount;

          // Add/remove empty state classes
          if (itemCount === 0) {
            cartBadge.classList.add("cart-empty");
            // Hide the badge when cart is empty
            cartBadge.style.display = "none";
          } else {
            cartBadge.classList.remove("cart-empty");
            // Show the badge when cart has items
            cartBadge.style.display = "flex";
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching cart data:", error);
      });
  }

  // Update cart badge when items are added to cart
  document.addEventListener("on:cart:add", function (event) {
    updateCartBadgeCount();
  });

  // Update cart badge after cart merge (when cart contents change)
  document.addEventListener("on:cart:after-merge", function (event) {
    updateCartBadgeCount();
  });

  // Initial update on page load
  updateCartBadgeCount();
});

document.addEventListener("DOMContentLoaded", function () {
  let feeVariantId = 55437964738885;

  let targetProductIds = [
    15104260145477, // CRISPACT probiotikai moterims, N30 paketėlių.
    15110799884613, // CRISPACT probiotikai moterims, N30*3 pakuotės
    15109035295045, // Rinkinys „PASIKARTOJANTI KANDIDOZĖ“
    15109044535621, // Rinkinys „PASIKARTOJANTI VAGINOZĖ“
  ];

  function syncPackagingFee() {
    console.log("syncPackagingFee");
    fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        // Count total quantity of target products
        const targetCount = cart.items
          .filter((item) => targetProductIds.includes(item.product_id))
          .reduce((sum, item) => sum + item.quantity, 0);

        // Find fee line in cart
        const feeItem = cart.items.find(
          (item) => item.variant_id === feeVariantId
        );
        const feeQty = feeItem ? feeItem.quantity : 0;

        // If quantity mismatch, update via /cart/update.js
        if (targetCount !== feeQty) {
          fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              updates: {
                [feeVariantId]: targetCount, // sets fee quantity, 0 removes it
              },
            }),
          }).then(() => {
            // reload cart to show updated fee
            document.dispatchEvent(
              new CustomEvent("dispatch:cart-drawer:refresh", {
                bubbles: true,
              })
            );

            console.log("cart-drawer:refresh");
          });
        }
      });
  }

  // Run on load
  syncPackagingFee();

  // Also run after AJAX cart updates (depends on theme’s cart implementation)
  document.addEventListener("on:cart:add", syncPackagingFee);
  document.addEventListener("on:cart:remove", syncPackagingFee);
  document.addEventListener("on:cart:after-merge", syncPackagingFee);
});

(function () {

  // Do not run on product pages
  if (document.body.classList.contains('template-product')) return;

  function addBuyButtons(root = document) {

    const blocks = root.querySelectorAll('.jdgm-product-name');

    blocks.forEach(block => {
      if (block.querySelector('.jdgm-buy-button')) return;

      const link = block.querySelector('a');
      if (!link || !link.href) return;

      const button = document.createElement('a');
      button.href = link.href;
      button.textContent = 'Pirkti';
      button.className = 'jdgm-buy-button btn';

      block.appendChild(button);
    });
  }

  // Initial attempt (in case slider is already there)
  addBuyButtons();

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;

        // Direct match
        if (node.matches && node.matches('.jdgm-product-name')) {
          addBuyButtons(node.parentElement);
        }

        // Nested match
        if (node.querySelectorAll) {
          addBuyButtons(node);
        }
      });
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();


document.addEventListener('DOMContentLoaded', function () {

const slide = document.querySelector('#shopify-section-template--29008266101061__slideshow_yeyrHx .slide-1');

  // Remove unwanted classes from elements inside the slide
  
    slide.classList.remove(
      'image-overlay',
      'image-overlay--bg-full',
      'image-overlay--bg-shadow',
      // 'height--fixed'
    );

    slide.style.position = 'relative';
    slide.style.height = 'auto';


    slide.querySelector('.text-overlay__text').style.textAlign = 'left';
  

  // Change title color
  slide.querySelectorAll('.text-overlay__title').forEach(title => {
    title.style.color = '#95525C';
    title.style.textShadow = 'none';
  });

  let count = 0;

  slide.querySelectorAll('.text-overlay__rte h1').forEach(title => {

    if (count == 0) {
      title.style.textShadow = 'none';
      count ++;
      return null;
    }


    title.style.color = '#A27C84';
    title.style.textShadow = 'none';
  
  })
});